import { useEffect, type ReactNode } from 'react'
import { checkWithin } from '../forms/checkForm'
import { COMPONENTS_CONFIRM_DIALOG_TEXTS as T } from '../../shared/texts'

// Reusable confirmation modal (reuses the .modal styling). Controlled: the parent
// keeps it open while busy and feeds back a server error (e.g. a 409) so the
// dialog stays open and shows the reason instead of closing.
//
// ⚠️ `onCancel` ist OPTIONAL, und daran haengt der Hinweis-Fall: Fehlt der Handler, gibt es
// nichts abzulehnen — dann zeigt der Dialog nur EINEN Knopf, und Escape wie Klick daneben
// laufen auf `onConfirm`. Das ist kein Sonderweg, sondern die Form fuer einen Dialog, der
// nur zur Kenntnis genommen wird.
//
// Gebaut, weil zwei verschieden beschriftete Knoepfe („Verstanden" / „Schliessen"), die
// dasselbe tun, eine Wahl versprechen, die es nicht gibt — der Betreiber hielt beim ersten
// Ansehen inne und fragte, was der Unterschied sei (09.09.2026). Absichtlich HIER und nicht
// mit eigenem Modal-Markup in der einen Maske: Eigenes Markup neben einer vorhandenen Regel
// ist genau das Muster, das uns bei der Feld-Optik zweimal eingeholt hat, und ein Hinweis
// wird nicht der letzte gewesen sein. Erhoben, nicht geschaetzt: Von 19 Verwendungen war
// dies die einzige mit gleichem onConfirm/onCancel.
interface Props {
    open: boolean
    title: string
    message?: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    danger?: boolean
    busy?: boolean
    error?: string
    onConfirm: () => void
    /** Weglassen macht daraus einen reinen Hinweis: ein Knopf, kein „Abbrechen". */
    onCancel?: () => void
}

export default function ConfirmDialog({
    open, title, message, confirmLabel = T.confirm, cancelLabel = T.cancel,
    danger, busy, error, onConfirm, onCancel,
}: Props) {
    // Ohne `onCancel` schliesst der Hinweis ueber `onConfirm` — sonst liesse er sich weder
    // mit Escape noch mit einem Klick daneben wegbekommen.
    const dismiss = onCancel ?? onConfirm

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) dismiss() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, busy, dismiss])

    if (!open) return null
    return (
        <div className="modal-backdrop" onMouseDown={() => !busy && dismiss()}>
            <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
                <h2>{title}</h2>
                {message && <p>{message}</p>}
                {error && <div className="notice err">{error}</div>}
                <div className="modal-actions">
                    {/* Fields inside the dialog (e.g. the refund fee) are checked first — forms/checkForm.ts. */}
                    <button type="button" className={danger ? 'danger' : ''} onClick={(e) => { if (checkWithin(e, '.modal')) onConfirm() }} disabled={busy}>
                        {busy ? '…' : confirmLabel}
                    </button>
                    {onCancel && (
                        <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
                            {cancelLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
